/**
 * HR · Saudization (Nitaqat).
 *
 * The share of Saudis among the direct employees as Nitaqat counts it — a
 * Saudi in full at the full-count wage, half at the lower one — against the
 * band the airport must hold, with the hires it would take to get there and
 * the Saudis whose pay keeps them from counting in full.
 *
 * The target is entered, not assumed: the band depends on the activity and
 * size the Ministry has the airport under, which Qiwa shows and this platform
 * does not know.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useHrSaudization } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Card, EmptyState, Field, Input, Skeleton, Table } from "../../design/components";
import type { HrSaudization } from "../../domain/hr";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { PersonLink, PreviewBar } from "./kit";
import { HrError, pct, RulesBasis, s, SampleBanner, sar, SUITE, Tiles } from "./shared";

export function HrSaudizationPage() {
  const session = useSession();
  const q = useHrSaudization();
  const z = q.data;

  useScreenContext(
    z
      ? {
          route: "/hr-saudization",
          title: "Saudization",
          summary: [
            z.pct == null
              ? t("No direct employee is on the register yet.")
              : t("Saudization stands at {p} of the direct workforce ({c} Saudis counted, {e} non-Saudis).", {
                  p: pct(z.pct),
                  c: z.counted,
                  e: z.expats,
                }),
            z.targetPct == null
              ? t("No Nitaqat target has been entered; enter it from Qiwa on the rules screen.")
              : z.hiresNeeded
                ? t("Reaching the {tgt} target takes {n} more Saudis counted in full.", {
                    tgt: pct(z.targetPct, 0),
                    n: z.hiresNeeded,
                  })
                : t("The {tgt} target is met.", { tgt: pct(z.targetPct, 0) }),
            t("{n} Saudis are paid under the full-count wage.", { n: z.belowWage.length }),
          ],
          offers: [
            {
              label: t("How many Saudis must we hire?"),
              ask: "how many Saudis do we need to hire to reach the Nitaqat target?",
            },
            { label: t("Which department is lowest?"), ask: "which department has the lowest Saudization?" },
          ],
          worksInFull: true,
        }
      : null,
  );

  return (
    <Page eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })} title="Saudization">
      <PreviewBar />
      <SampleBanner />
      {q.isLoading && <Skeleton rows={8} />}
      {q.error && <HrError error={q.error} retry={() => void q.refetch()} />}
      {z && (
        <>
          <Tiles
            items={[
              {
                key: "pct",
                label: t("Saudization (Nitaqat)"),
                value: pct(z.pct),
                sub: z.targetPct != null ? t("target {tgt}", { tgt: pct(z.targetPct, 0) }) : t("no target entered"),
                tone: z.targetPct == null || z.pct == null ? undefined : z.pct >= z.targetPct ? "good" : "bad",
              },
              {
                key: "counted",
                label: t("Saudis counted"),
                value: z.counted,
                sub: t("{n} Saudi direct employees", { n: z.saudis }),
              },
              { key: "expats", label: t("Non-Saudi direct employees"), value: z.expats },
              {
                key: "hires",
                label: t("Saudi hires to the target"),
                value: z.hiresNeeded ?? "—",
                sub: z.hiresNeeded == null ? t("enter the target first") : t("counted in full, nobody leaving"),
                tone: z.hiresNeeded ? "warn" : z.hiresNeeded === 0 ? "good" : undefined,
              },
            ]}
          />
          <Meter z={z} />
          {z.outsourced > 0 && (
            <p className={s.note}>
              {t(
                "{n} outsourced staff aren't counted here; they count for their contractor.",
                {
                  n: z.outsourced,
                },
              )}
            </p>
          )}
          <WhatIf z={z} />
          <div className={s.split}>
            <ByDepartment z={z} />
            <BelowWage z={z} />
          </div>
          <Card title={t("Nationality mix")} sub={t("Direct employees, by nationality.")}>
            <Table>
              <thead>
                <tr>
                  <th>{t("Nationality")}</th>
                  <th className={s.num}>{t("People")}</th>
                </tr>
              </thead>
              <tbody>
                {z.byNationality.map((n) => (
                  <tr key={n.nationality}>
                    <td className={s.mono}>{n.nationality}</td>
                    <td className={s.num}>{n.heads}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
          <p className={s.note}>
            {t("How it is worked out:")} {t(z.method)}
          </p>
          <RulesBasis basis={z.rulesBasis} />
        </>
      )}
    </Page>
  );
}

function Meter({ z }: { z: HrSaudization }) {
  if (z.pct == null) return <EmptyState>{t("No direct employee is on the register yet.")}</EmptyState>;
  return (
    <Card title={t("Where the airport stands")}>
      <div className={s.meter} style={{ height: 14, position: "relative" }} aria-label={t("Saudization (Nitaqat)")}>
        <span
          style={{
            width: `${Math.min(100, z.pct)}%`,
            background: z.targetPct != null && z.pct < z.targetPct ? "var(--amber)" : "var(--green)",
          }}
        />
        {z.targetPct != null && (
          <i
            title={t("target {tgt}", { tgt: pct(z.targetPct, 0) })}
            style={{
              position: "absolute",
              insetInlineStart: `${Math.min(100, z.targetPct)}%`,
              top: -3,
              bottom: -3,
              width: 2,
              background: "var(--ink)",
            }}
          />
        )}
      </div>
      <p className={s.note}>
        {z.targetPct == null ? (
          <>
            {t("Enter the airport's Nitaqat target from Qiwa to see the gap.")}{" "}
            <Link to="/hr-rules">{t("Saudi labour rules")}</Link>
          </>
        ) : z.gapPct != null && z.gapPct > 0 ? (
          t("{gap} points below the target.", { gap: z.gapPct.toFixed(1) })
        ) : (
          t("At or above the target.")
        )}
      </p>
    </Card>
  );
}

/** Hire or lose people and see the share move; nothing is saved. */
function WhatIf({ z }: { z: HrSaudization }) {
  const [hire, setHire] = useState("0");
  const [lose, setLose] = useState("0");
  const h = Math.max(0, Math.floor(Number(hire) || 0));
  const l = Math.min(z.expats, Math.max(0, Math.floor(Number(lose) || 0)));
  const den = z.counted + h + z.expats - l;
  const next = den ? Math.round(((z.counted + h) / den) * 1000) / 10 : null;
  return (
    <Card
      title={t("What if")}
      sub={t("Saudi hires at full-count wage; non-Saudi contracts not renewed. Not saved.")}
    >
      <div className={s.form}>
        <Field label="Saudis hired">
          <Input
            value={hire}
            onChange={(e) => setHire(e.target.value)}
            inputMode="numeric"
            data-testid="hr-whatif-hire"
          />
        </Field>
        <Field label="Non-Saudi contracts not renewed">
          <Input value={lose} onChange={(e) => setLose(e.target.value)} inputMode="numeric" />
        </Field>
        <div>
          <div className={s.tileLabel}>{t("Saudization would be")}</div>
          <div className={s.tileN} data-testid="hr-whatif-result">
            {pct(next)}
          </div>
        </div>
      </div>
    </Card>
  );
}

function ByDepartment({ z }: { z: HrSaudization }) {
  return (
    <Card title={t("By department")}>
      <Table>
        <thead>
          <tr>
            <th>{t("Department")}</th>
            <th className={s.num}>{t("Saudis counted")}</th>
            <th className={s.num}>{t("Non-Saudis")}</th>
            <th className={s.num}>{t("Share")}</th>
          </tr>
        </thead>
        <tbody>
          {z.byDepartment.map((d) => (
            <tr key={d.key}>
              <td>{d.key}</td>
              <td className={s.num}>{d.counted}</td>
              <td className={s.num}>{d.expats}</td>
              <td
                className={s.num}
                style={
                  z.targetPct != null && d.pct != null && d.pct < z.targetPct ? { color: "var(--red)" } : undefined
                }
              >
                {pct(d.pct)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function BelowWage({ z }: { z: HrSaudization }) {
  return (
    <Card
      title={t("Saudis under the full-count wage")}
      sub={t("Full count from {full}, half from {half} (basic + housing).", {
        full: sar(z.fullAtWage),
        half: sar(z.halfAtWage),
      })}
    >
      {z.belowWage.length === 0 ? (
        <EmptyState>{t("Every Saudi on the register counts in full.")}</EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>{t("Name")}</th>
              <th>{t("Department")}</th>
              <th className={s.num}>{t("Basic + housing")}</th>
              <th className={s.num}>{t("Counts as")}</th>
              <th className={s.num}>{t("Raise to count in full")}</th>
            </tr>
          </thead>
          <tbody>
            {z.belowWage.map((b) => (
              <tr key={b.employeeId}>
                <td>
                  <PersonLink id={b.employeeId} name={b.name} />
                </td>
                <td>{b.department ?? "—"}</td>
                <td className={s.num}>{sar(b.wage)}</td>
                <td className={s.num}>{b.weight}</td>
                <td className={s.num}>{sar(z.fullAtWage - b.wage)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
